import { useEffect, useState } from 'react';
import { AnnotationRequestError, toWireItems } from '@apis/documentAnnotations';
import { useSaveAnnotation } from '@components/Pages/DocumentsPage/hooks/useDocumentAnnotations';
import { useDrawingModel } from '../AnnotationsBody/AnnotationDraft/hooks/useDrawingModel';

/** Why a save failed: the drawing is too large (400), the file refuses it
 *  (409, so retrying won't help), or anything else, worth retrying. */
export type DraftFailure = 'tooLarge' | 'refused' | 'failed';

function failureOf(error: Error): DraftFailure {
  if (!(error instanceof AnnotationRequestError)) return 'failed';
  if (error.status === 400) return 'tooLarge';
  return error.status === 409 ? 'refused' : 'failed';
}

/**
 * The annotation being drawn: the drawing model, its note, and the two ways out.
 * It lives at the page because two surfaces need it — the tools over the
 * document, the note and the commit in the sidebar.
 */
export function useAnnotationDraft(
  siteId: string,
  fileId: string,
  currentPage: number,
  onSaved: () => void,
  onShowPage?: (page: number) => void,
) {
  const markup = useDrawingModel(currentPage, onShowPage);
  const save = useSaveAnnotation(siteId, fileId);
  const [note, setNote] = useState('');

  // Unseen marks are unrecoverable, so closing the tab asks.
  useEffect(() => {
    if (!markup.hasItems) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [markup.hasItems]);

  /** A session that ended must not leave its marks waiting in the next one. */
  function reset() {
    markup.reset();
    setNote('');
    save.reset();
  }

  /** A failed save leaves the draft as it is, with its error shown. */
  function commit() {
    if (!markup.hasItems || save.isPending) return;
    save.mutate(
      { note: note.trim(), items: toWireItems(markup.itemsToSave) },
      { onSuccess: () => { reset(); onSaved(); } },
    );
  }

  return {
    markup,
    note,
    setNote,
    commit,
    reset,
    saving: save.isPending,
    failure: save.error ? failureOf(save.error) : null,
    canSave: markup.hasItems && !save.isPending,
    /** Marks exist, so leaving must ask first. */
    dirty: markup.hasItems,
  };
}

export type AnnotationDraft = ReturnType<typeof useAnnotationDraft>;
